import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, ArrowRight } from 'lucide-react';
import { requestOnboardingSlot, releaseOnboardingSlot } from '../onboardingQueue';
import { useAuth } from '../contexts/AuthContext';
import { needsRealEmail } from '../utils/needsRealEmail';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SEEN_KEY = 'betohalics:whats-new-seen-version';

/**
 * Auto-shows once after each PWA update, then dismisses for that version.
 * - On mount: fetches /api/whats-new
 * - If `version` is different from what's stored in localStorage → opens modal
 * - On dismiss: stores current version → never shows again until next deploy
 *
 * Can also be opened on demand by dispatching `window.dispatchEvent(new CustomEvent('betohalics:open-whats-new'))`.
 */
const WhatsNewModal = () => {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [data, setData] = useState(null);

    useEffect(() => {
        let alive = true;
        const fetchAndMaybeShow = async (forceShow = false) => {
            try {
                const { data: payload } = await axios.get(`${API}/whats-new?limit=3`);
                if (!alive) return;
                if (!payload?.entries?.length) return;
                setData(payload);
                if (forceShow) {
                    setOpen(true);
                    return;
                }
                const seen = (() => { try { return localStorage.getItem(SEEN_KEY); } catch { return null; } })();
                if (payload.version && seen !== payload.version) {
                    // Queue behind the higher-priority onboarding modals so
                    // they never stack on first entry.
                    requestOnboardingSlot('whats-new', 10, () => setOpen(true));
                }
            } catch { /* silent */ }
        };

        // Small delay so it never pops up DURING login/auth
        const t = setTimeout(() => fetchAndMaybeShow(false), 1800);

        const onManualOpen = () => fetchAndMaybeShow(true);
        window.addEventListener('betohalics:open-whats-new', onManualOpen);

        return () => {
            alive = false;
            clearTimeout(t);
            window.removeEventListener('betohalics:open-whats-new', onManualOpen);
            releaseOnboardingSlot('whats-new');
        };
    }, []);

    const dismiss = () => {
        if (data?.version) {
            try { localStorage.setItem(SEEN_KEY, data.version); } catch { /* ignore */ }
        }
        setOpen(false);
        releaseOnboardingSlot('whats-new');
    };

    if (!data || !data.entries?.length) return null;
    if (needsRealEmail(user)) return null;

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100] flex items-center justify-center px-4 sm:px-6 py-8"
                    style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
                    onClick={dismiss}
                    data-testid="whats-new-overlay"
                >
                    <motion.div
                        initial={{ opacity: 0, y: 24, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 12, scale: 0.96 }}
                        transition={{ type: 'spring', stiffness: 320, damping: 24 }}
                        onClick={(e) => e.stopPropagation()}
                        className="a11y-font relative w-full max-w-md bg-[#121212] border border-[#FFD700]/40 rounded-2xl overflow-hidden"
                        data-testid="whats-new-modal"
                    >
                        {/* Header */}
                        <div className="relative px-6 pt-6 pb-4 border-b border-white/5">
                            <button
                                onClick={dismiss}
                                className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center text-[#A0AAB2] hover:text-white hover:bg-white/5"
                                aria-label="Close"
                                data-testid="whats-new-close"
                            >
                                <X className="w-5 h-5" />
                            </button>
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-12 h-12 rounded-full bg-[#FFD700]/15 flex items-center justify-center">
                                    <Sparkles className="w-6 h-6 text-[#FFD700]" />
                                </div>
                                <div>
                                    <p className="a11y-section-eyebrow">WHAT'S NEW</p>
                                    <h2 className="a11y-heading text-xl text-white">Fresh updates for you</h2>
                                </div>
                            </div>
                        </div>

                        {/* Entries */}
                        <div className="max-h-[60vh] overflow-y-auto px-6 py-4 space-y-5">
                            {data.entries.slice(0, 3).map((e, idx) => (
                                <div key={`${e.date}-${idx}`} className="space-y-2">
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-[10px] font-bold text-[#A0AAB2] tracking-[0.18em]">{e.date}</span>
                                    </div>
                                    <h3 className="text-base font-bold text-white leading-tight">{e.title}</h3>
                                    {e.tagline && (
                                        <p className="text-sm text-[#cccccc] leading-snug">{e.tagline}</p>
                                    )}
                                    {!e.tagline && e.bullets?.length > 0 && (
                                        <ul className="space-y-1.5 mt-2">
                                            {e.bullets.slice(0, 2).map((b, i) => (
                                                <li key={i} className="text-sm text-[#cccccc] leading-snug flex gap-2">
                                                    <span className="text-[#FFD700] flex-shrink-0 mt-0.5">·</span>
                                                    <span>{b}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Footer CTA */}
                        <div className="px-6 py-4 border-t border-white/5 flex items-center justify-end gap-2">
                            <button
                                onClick={dismiss}
                                className="px-5 py-2.5 rounded-full bg-[#FFD700] hover:bg-[#FFC700] text-black font-black text-sm a11y-tap flex items-center gap-1.5"
                                data-testid="whats-new-cta"
                            >
                                Got it
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default WhatsNewModal;
